import { BehaviorParameters } from 'rangeConfig/@types';
import { Connection, Connector, Entity, HierarchyUtils, Vector } from '@inter-ikea-core/core';
import addProduct from 'rangeConfig/shared/behaviors/addProduct';
import {
  CoreApi,
  ErrorMessages,
  Product,
  ProductType,
  calculatePartialDistance
} from '@inter-ikea-storageone/range';
import {
  getAllConnectorsThatOffer,
  getCurrentConnectionsOfType
} from 'rangeConfig/shared/utils/connectors';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';

type Config = { key: string; hostLimit: number; targetPosition?: Partial<Vector>; amount: number };

const getConnectorCandidates = (coreApi: CoreApi, config: Config, productType: ProductType) => {
  const currentConnections = getCurrentConnectionsOfType(coreApi, productType as ProductType);
  const allConnectors = getAllConnectorsThatOffer(coreApi, config.key);

  const occupiedConnectors = allConnectors.filter(
    c => !isConnectorAvailable(c, currentConnections)
  );

  const meanPosition = occupiedConnectors
    .map(c => c.getWorldPositionFromOwnerEntity(c.host).div(occupiedConnectors.length))
    .reduce((acc, curr) => (acc ? acc.plus(curr) : curr), null as null | Vector);

  const connectorCandidates = allConnectors
    .filter(
      c =>
        currentConnections.filter(connection => connection.hostConnectorEntity.id === c.host.id)
          .length < config.hostLimit
    )
    .map(c =>
      Object.assign(c, {
        distanceToOthers: meanPosition
          ? calculatePartialDistance(c.getWorldPositionFromOwnerEntity(c.host), meanPosition)
          : 0,
        distanceToTarget: config.targetPosition
          ? calculatePartialDistance(
              c.getWorldPositionFromOwnerEntity(c.host),
              config.targetPosition
            )
          : 0
      })
    );

  const sortedCandidates = connectorCandidates.sort((a, b) => {
    const distanceToTargetDiff = Math.round(a.distanceToTarget - b.distanceToTarget);
    const distanceToOthersDiff = Math.round(a.distanceToOthers - b.distanceToOthers);
    return !distanceToTargetDiff ? distanceToOthersDiff : distanceToTargetDiff;
  });

  return sortedCandidates;
};

const isConnectorAvailable = (connector: Connector & { host: Entity }, connections: Connection[]) =>
  !connections.find(
    connection =>
      connection.hostConnectorEntity.id === connector.host.id &&
      connection.hostConnector.key === connector.key
  );

const addAccessoryBehavior = (params: BehaviorParameters) => {
  const { coreApi, rootState, props } = params;
  const { inMarket: products } = rootState.products as { inMarket: Product[] };
  const { ageGroup } = rootState.layout;
  const selectedEntity = coreApi.entities.findSelectedEntity();

  const productId = props.coreProductIds?.length ? props.coreProductIds[0] : props.coreProductId;
  const productType = products.find(({ coreProductId }) => coreProductId === productId)?.type;

  const eyeHeight = ageGroup === '3-7' ? 1050 : ageGroup === '8-14' ? 1400 : 1700;

  const productTypeToConfig: Record<string, Config> = {
    [ProductType.SMASTAD_LABEL_HOLDER]: {
      amount: 5,
      hostLimit: 1,
      key: 'attach-label-holder'
    },
    [ProductType.SMASTAD_HOOK_CLIP]: {
      amount: 2,
      hostLimit: 2,
      key: 'attach-hook-clip',
      targetPosition: { Y: eyeHeight }
    },
    [ProductType.SMASTAD_MIRROR]: {
      amount: 1,
      hostLimit: 1,
      key: 'attach-mirror',
      targetPosition: { Y: eyeHeight }
    }
  };

  const config = productType && productTypeToConfig[productType];
  if (config) {
    const connectorCandidates = getConnectorCandidates(coreApi, config, productType as ProductType);

    let hosts: number[] = [];

    const targetCandidates = selectedEntity
      ? connectorCandidates.filter(
          ({ host }) =>
            selectedEntity.id === host.id || HierarchyUtils.isDescendantOf(host, selectedEntity)
        )
      : connectorCandidates;

    if (targetCandidates.length === 0) {
      const placementResult = coreApi.products.add(productId);
      if (!placementResult.valid && placementResult.errors && placementResult.errors.length > 0) {
        const placementAttemptErrorMessage = !hideNoFramesInSceneErrorMessage(coreApi)
          ? ErrorMessages.NoFramesInScene
          : ErrorMessages.SmastadExteriorAccessoryOnDoorsOnly;
        coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
      }

      return;
    }

    targetCandidates.some(({ host, key }) => {
      if (hosts.filter(id => id === host.id).length >= config.hostLimit) return false;

      const { valid } = coreApi.products.addToConnector(productId, host, 'attach', key);

      hosts = valid ? hosts.concat(host.id) : hosts;

      const amount = selectedEntity ? 1 : config.amount;

      return valid && amount === hosts.length;
    });

    return;
  }

  return addProduct(params);
};

export default addAccessoryBehavior;
